import { useAffectedEntities } from '@/hooks/useAffectedEntities';
import { AGENT_API_URL } from '@/services/agent/config';
import { getCurrentIdToken, memoizedRefreshOAuthToken } from '@/services/auth/oauthService';
import useAgentChatStore, { AgentMessage } from '@/stores/useAgentChatStore';
import { useCallback, useRef } from 'react';

/**
 * Hook for managing agent chat SSE connection and message sending.
 */
export function useAgentChat() {
  const {
    messages,
    conversationId,
    isSending,
    addMessage,
    updateLastAssistantMessage,
    setConversationId,
    setIsSending,
  } = useAgentChatStore();

  const { processAffectedEntities } = useAffectedEntities();
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isSending) return;

      // Add user message to store
      const userMessage: AgentMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
        createdAt: new Date().toISOString(),
      };
      addMessage(userMessage);
      setIsSending(true);

      // Add placeholder assistant message for streaming
      const assistantMessage: AgentMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
        isStreaming: true,
      };
      addMessage(assistantMessage);

      const token = getCurrentIdToken();
      if (!token) {
        updateLastAssistantMessage('Authentication error. Please refresh the page.');
        setIsSending(false);
        return;
      }

      // Abort any existing connection
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      try {
        const makeRequest = async (authToken: string) =>
          fetch(`${AGENT_API_URL}/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              message: text,
              conversation_id: conversationId,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
            signal: abortControllerRef.current!.signal,
          });

        let response = await makeRequest(token);

        // Retry with refreshed token on 401
        if (response.status === 401) {
          const refreshed = await memoizedRefreshOAuthToken();
          if (refreshed) {
            const newToken = getCurrentIdToken();
            if (newToken) {
              response = await makeRequest(newToken);
            }
          } else {
            updateLastAssistantMessage('Session expired. Please refresh the page.');
            setIsSending(false);
            return;
          }
        }

        if (!response.ok) {
          const errorText = await response.text();
          updateLastAssistantMessage(
            response.status === 403
              ? 'Agent access is not available for your account.'
              : `Error: ${errorText || response.statusText}`,
          );
          setIsSending(false);
          return;
        }

        // Process SSE stream
        const reader = response.body?.getReader();
        if (!reader) {
          updateLastAssistantMessage('Failed to establish connection.');
          setIsSending(false);
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        let done = false;
        while (!done) {
          const result = await reader.read();
          done = result.done;
          const value = result.value;
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                switch (data.type) {
                  case 'delta':
                    updateLastAssistantMessage(data.content);
                    break;

                  case 'tool_result':
                    if (data.affected_entities?.length > 0) {
                      processAffectedEntities(data.affected_entities);
                    }
                    break;

                  case 'done':
                    if (data.conversation_id) {
                      setConversationId(data.conversation_id);
                    }
                    if (data.affected_entities?.length > 0) {
                      processAffectedEntities(data.affected_entities);
                    }
                    break;

                  case 'error':
                    updateLastAssistantMessage(`\n\nError: ${data.content}`);
                    break;
                }
              } catch {
                // Ignore malformed SSE events
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          updateLastAssistantMessage('\n\nConnection error. Please try again.');
        }
      } finally {
        setIsSending(false);
        // Mark streaming as complete by updating the last message
        useAgentChatStore.setState((state) => {
          const messages = [...state.messages];
          const lastIdx = messages.length - 1;
          if (lastIdx >= 0 && messages[lastIdx].role === 'assistant') {
            messages[lastIdx] = { ...messages[lastIdx], isStreaming: false };
          }
          return { messages };
        });
      }
    },
    [
      isSending,
      conversationId,
      addMessage,
      updateLastAssistantMessage,
      setConversationId,
      setIsSending,
      processAffectedEntities,
    ],
  );

  const stopGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsSending(false);
  }, [setIsSending]);

  return {
    messages,
    conversationId,
    isSending,
    sendMessage,
    stopGeneration,
  };
}
